import { container, DependencyContainer, InjectionToken, Lifecycle } from "@launchtray/tsyringe-async";
import { Emitter } from "@pixi/particle-emitter";
import { OrderedTextureWithStartPositionBehavior } from "by/gamefactory/animations/behaviors/OrderedTextureWithStartPositionBehavior";
import { RotateToPointBehaviour } from "by/gamefactory/animations/behaviors/RotateToPointBehavior";
import { PromiseCompletionSource } from "by/gamefactory/shared/async/PromiseCompletionSource";
import { Renderer } from "pixi.js";
import { AnalyticsProvider } from "../../analytics/AnalyticsProvider";
import { GameInfo } from "../../analytics/GameInfo";
import { ICanvasScaler } from "../../canvas/ICanvasScaler";
import { SceneManager } from "../../scene/SceneManager";
import { LocalStorage } from "../../tools/variableSaver/LocalStorage";
import { VariableSaver } from "../../tools/variableSaver/VariableSaver";
import { Logger } from "../logger/Logger";
import { DefaultNetworkConfig } from "../networking/config/impl/DefaultNetworkConfig";
import { PersonalOfferReason } from "../networking/data/responses/personaloffer/PersonalOfferReason";
import { NetworkClient } from "../networking/NetworkClient";
import { ApplePayApi } from "../purchases/ApplePayApi";
import { ApplePayProvider } from "../purchases/ApplePayProvider";
import { AsyncOperationNotification } from "../scenes/common/components/AsyncOperationNotification";
import { FirstTimeExperienceController } from "../scenes/common/controller/FirstTimeExperienceController";
import { ApplicationModel } from "../scenes/common/models/ApplicationModel";
import { IBuildingModel } from "../scenes/common/models/Building/IBuildingModel";
import { FirstTimeExperienceModel } from "../scenes/common/models/FirstTimeExperienceModel";
import { GuestAuthInfo } from "../scenes/common/models/GuestAuthInfo";
import { GuestModel } from "../scenes/common/models/GuestModel";
import { LatestNewsModel } from "../scenes/common/models/latestnews/LatestNewsModel";
import { PlayerModel } from "../scenes/common/models/PlayerModel";
import { IRenterPolicyModel } from "../scenes/common/models/Renters/IRenterPolicyModel";
import { RenterPolicyModel } from "../scenes/common/models/Renters/RenterPolicyModel";
import { TopPanel } from "../scenes/game/components/toppanel/TopPanel";
import { GameController } from "../scenes/game/controllers/GameController";
import { PushDialogData } from "../scenes/game/flow/impl/dialogAction/PushDialogData";
import { AmazingWinAction } from "../scenes/game/flow/impl/diceActions/AmazingWinAction";
import { DeadRollMediumCoinsAction } from "../scenes/game/flow/impl/diceActions/DeadRollMediumCoinsAction";
import { DeadRollSmallCoinsAction } from "../scenes/game/flow/impl/diceActions/DeadRollSmallCoinsAction";
import { DiceComboAction } from "../scenes/game/flow/impl/diceActions/DiceComboAction";
import { RaidAction } from "../scenes/game/flow/impl/diceActions/raidAction/RaidAction";
import { StealAction } from "../scenes/game/flow/impl/diceActions/stealAction/StealAction";
import { VisitAction } from "../scenes/game/flow/impl/diceActions/visitAction/VisitAction";
import { GameModel } from "../scenes/game/models/GameModel";
import { GameViewModel } from "../scenes/game/models/GameViewModel";
import { NewsModel } from "../scenes/game/models/NewsModel";
import { AmazingWinCoinsPopupScene } from "../scenes/popups/awazingwincoins/AmazingWinCoinsPopupScene";
import { AmazingWinCoinsController } from "../scenes/popups/awazingwincoins/controllers/AmazingWinCoinsController";
import { BuildingRankUpPopupScene } from "../scenes/popups/buildingRankUp/BuildingRankUpPopupScene";
import { BuildingRankUpController } from "../scenes/popups/buildingRankUp/controller/BuildingRankUpController";
import { IBuildingRankUpModel } from "../scenes/popups/buildingRankUp/model/IBuildingRankUpModel";
import { BuildingUpgradeItemPopupScene } from "../scenes/popups/buildingUpgradeItem/BuildingUpgradeItemPopupScene";
import { UpgradeItemSceneController } from "../scenes/popups/buildingUpgradeItem/controller/UpgradeItemSceneController";
import { CheckOutFixUpgradePopupScene } from "../scenes/popups/checkoutfixupgrade/CheckOutFixUpgradePopupScene";
import { CheckOutShopPopupScene } from "../scenes/popups/checkoutshop/CheckOutShopPopupScene";
import { CookiesPopupScene } from "../scenes/popups/cookies/CookiesPopupScene";
import { DiceRollOfferController } from "../scenes/popups/dicerollsoffer/controller/DiceRollOfferController";
import { DiceRollsOfferPopupScene } from "../scenes/popups/dicerollsoffer/DiceRollsOfferPopupScene";
import { DiceRollsOfferModel } from "../scenes/popups/dicerollsoffer/model/DiceRollsOfferModel";
import { GiftBoxPopupController } from "../scenes/popups/gift/controller/GiftBoxPopupController";
import { GiftBoxPopupScene } from "../scenes/popups/gift/GiftBoxPopupScene";
import { LandlordRenterInfoPopupScene } from "../scenes/popups/landlordrenterinfo/LandlordRenterInfoPopupScene";
import { AuthController } from "../scenes/popups/login/controllers/AuthController";
import { AuthModel } from "../scenes/popups/login/models/AuthModel";
import { MediumWinCoinsController } from "../scenes/popups/mediumlwincoins/controllers/MediumWinCoinsController";
import { MediumWinCoinsPopupScene } from "../scenes/popups/mediumlwincoins/MediumWinCoinsPopupScene";
import { PiggyController } from "../scenes/popups/piggy/controller/PiggyController";
import { PiggyPopupScene } from "../scenes/popups/piggy/PiggyPopupScene";
import { PiggyInfoType } from "../scenes/popups/piggyinfo/components/PiggyInfoType";
import { PiggyInfoPopupScene } from "../scenes/popups/piggyinfo/PiggyInfoPopupScene";
import { PushController } from "../scenes/popups/push/controllers/PushController";
import { PushModel } from "../scenes/popups/push/model/PushModel";
import { PushPopupScene } from "../scenes/popups/push/PushPopupScene";
import { RaidController } from "../scenes/popups/raid/controllers/RaidController";
import { RaidPopupScene } from "../scenes/popups/raid/RaidPopupScene";
import { RenterPolicyType } from "../scenes/popups/renterpolicy/components/RenterPolicyType";
import { RenterPolicyPopupScene } from "../scenes/popups/renterpolicy/RenterPolicyPopupScene";
import { SmallWinCoinsController } from "../scenes/popups/smallwincoins/controllers/SmallWinCoinsController";
import { SmallWinCoinsPopupScene } from "../scenes/popups/smallwincoins/SmallWinCoinsPopupScene";
import { SocialCommunitiesPopupScene } from "../scenes/popups/socialcommunities/socialcommunities/SocialCommunitiesPopupScene";
import { SocialCommunitiesIntroPopupScene } from "../scenes/popups/socialcommunities/socialcommunitiesintro/SocialCommunitiesIntroPopupScene";
import { StealController } from "../scenes/popups/steal/controllers/StealController";
import { StealPopupScene } from "../scenes/popups/steal/StealPopupScene";
import { UserLevelUpController } from "../scenes/popups/userlevelup/controller/UserLevelUpController";
import { IUserLevelUpModel } from "../scenes/popups/userlevelup/model/IUserLevelUpModel";
import { UserLevelUpPopupScene } from "../scenes/popups/userlevelup/UserLevelUpPopupScene";
import { VisitPopupScene } from "../scenes/popups/visit/VisitPopupScene";
import { ResourcesInitializer } from "../scenes/preloader/initializers/ResourcesInitializer";
import { SettingsController } from "../settings/SettingsController";
import { SettingsModel } from "../settings/SettingsModel";
import { IContextResolver } from "./IContextResolver";
import { NotificationLayerProvider } from "./providers/NotificationLayerProvider";
import { PiggyBankModel } from "../scenes/common/models/piggybank/PiggyBankModel";
import { CheckoutPiggyPopupScene } from "../scenes/popups/checkoutpiggy/CheckoutPiggyPopupScene";
import { CurrentPiggyState } from "../scenes/popups/piggy/components/piggycontentview/CurrentPiggyContentView";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { Web3Config } from "../../web3/config/Web3Config";
import { PiggyAnimationPromiseHolder } from "../scenes/popups/buildingUpgradeItem/components/PiggyAnimationPromiseHolder";
import { PreloaderModule } from "../scenes/preloader/module/PreloaderModule";
import { MyWalletModule } from "../scenes/popups/myWallet/MyWalletModule";
import { ParamDescription } from "./module/ParamDescription";
import { LoginModule } from "../scenes/popups/login/LoginModule";
import { NotificationMessagesModule } from "../scenes/popups/notificationMessages/NotificationMessagesModule";
import { ShopModule } from "../scenes/popups/shop/ShopModule";
import { ConnectionModel } from "../networking/ConnectionModel";
import { PaymentUrlResolver } from "../../web3/utils/PaymentUrlResolver";
import { MyBuildingsModule } from "../scenes/popups/mybuildings/MyBuildingsModule";
import { IModule } from "./module/IModule";
import { HourlyBonusModule } from "../scenes/popups/hourlyBonus/HourlyBonusModule";
import { FortuneWheelModel } from "../scenes/popups/hourlyBonus/model/FortuneWheelModel";
import { MarketplaceModule } from "../scenes/popups/marketplace/MarketplaceModule";
import { CancellationToken } from "async/CancellationToken";
import { LatestNewsModule } from "../scenes/popups/latestNews/LatestNewsModule";
import { IPlayerModel } from "../scenes/common/models/IPlayerModel";
import { RenterQuestIntroScene } from "../scenes/popups/renterquestintro/RenterQuestIntroScene";
import { UserProfileModule } from "../scenes/popups/userprofile/UserProfileModule";
import { AcceptOperationNotification } from "../scenes/common/components/AcceptOperationNotification";
import { InventoriesHolder } from "../../web3/models/InventoriesHolder";
import { VisitPopupController } from "../scenes/popups/visit/controller/VisitPopupController";
import { GiftResourcePackage } from "../scenes/popups/gift/resources/GiftResourcePackage";
import { UserProfileResourcePackage } from "../scenes/popups/userprofile/resources/UserProfileResourcePackage";
import { VisitResourcePackage } from "../scenes/popups/visit/resources/VisitResourcePackage";
import { AmazingCoinsWinResourcePackage } from "../scenes/popups/awazingwincoins/resources/AmazingCoinsWinResourcePackage";
import { StealResourcePackage } from "../scenes/popups/steal/resources/StealResourcePackage";
import { RaidResourcePackage } from "../scenes/popups/raid/resources/RaidResourcePackage";
import { UserLevelUpResourcePackage } from "../scenes/popups/userlevelup/resources/UserLevelUpResourcePackage";
import { PushResourcePackage } from "../scenes/popups/push/resources/PushResourcePackage";
import { RenterPolicyResourcePackage } from "../scenes/popups/renterpolicy/resources/RenterPolicyResourcePackage";
import { LandlordRenterInfoResourcePackage } from "../scenes/popups/landlordrenterinfo/resources/LandlordRenterInfoResourcePackage";
import { SocialCommunitiesResourcePackage } from "../scenes/popups/socialcommunities/resources/SocialCommunitiesResourcePackage";
import { EthL1PaymentProcessingModule } from "../scenes/popups/ethL1paymentprocessing/EthL1PaymentProcessingModule";
import { Erc20PaymentProcessingModule } from "../scenes/popups/ethL1paymentprocessing/Erc20PaymentProcessingModule";
import { DefaultMessageOptions, DefaultMessagesScene } from "../scenes/popups/defaultmessage/DefaultMessagesScene";
import { LeaderboardIntroScene } from "../scenes/popups/leaderboardintro/LeaderboardIntroScene";
import { FortuneWheelController } from "../scenes/popups/hourlyBonus/mainHourlyBonus/controller/FortuneWheelController";
import { TournamentModel } from "../scenes/common/models/tournament/TournamentModel";
import { Sentry } from "../../analytics/Sentry";
import { ThirdWebAnalytic } from "../../web3/utils/ThirdWebAnalytic";

export class CityverseTycoonContext implements IContextResolver {
    private readonly internalContainer: DependencyContainer;

    public constructor() {
        this.internalContainer = container.createChildContainer();
    }

    public build(
        canvasScaler: ICanvasScaler,
        sceneManager: SceneManager,
        gameInfo: GameInfo,
        renderer: Renderer,
        waitGuestInfo: PromiseCompletionSource<GuestAuthInfo>,
        sentry: Sentry,
        logger: Logger
    ): void {
        this.internalContainer.registerInstance("ICanvasScaler", canvasScaler);
        this.internalContainer.registerInstance(SceneManager, sceneManager);
        this.internalContainer.registerInstance(GameInfo, gameInfo);
        this.internalContainer.registerInstance(Web3Config, new Web3Config(gameInfo));
        this.internalContainer.registerInstance(Renderer, renderer);
        this.internalContainer.registerInstance("INetworkConfig", new DefaultNetworkConfig(gameInfo));
        this.internalContainer.registerInstance("WaitForGuestInfo", waitGuestInfo);
        this.internalContainer.registerInstance(Sentry, sentry);
        this.internalContainer.registerInstance("ILogger", logger);
        this.internalContainer.registerSingleton("INetworkClient", NetworkClient);
        this.internalContainer.registerSingleton(Web3Model, Web3Model);
        this.internalContainer.registerSingleton(ThirdWebAnalytic, ThirdWebAnalytic);
        
        this.internalContainer.registerSingleton(InventoriesHolder, InventoriesHolder);
        this.internalContainer.register("IInventory", {
            useFactory: async () => {
                return await this.resolve(InventoriesHolder);
            }
        });

        this.internalContainer.registerSingleton(PaymentUrlResolver, PaymentUrlResolver);
        this.internalContainer.registerSingleton("IPaymentsProvider", ApplePayProvider);
        this.internalContainer.registerSingleton("IApplePayApi", ApplePayApi);
        this.internalContainer.registerSingleton(VariableSaver, VariableSaver);
        this.internalContainer.registerSingleton("IPlayerModel", PlayerModel);
        this.internalContainer.registerSingleton("IFirstTimeExperienceModel", FirstTimeExperienceModel);
        this.internalContainer.register("IFirstTimeExperienceController", FirstTimeExperienceController, { lifecycle: Lifecycle.ResolutionScoped });
        this.internalContainer.registerSingleton("IAnalyticProviderWrapper", AnalyticsProvider);
        this.internalContainer.registerSingleton("IFortuneWheelController", FortuneWheelController);
        this.internalContainer.registerSingleton("IFortuneWheelModel", FortuneWheelModel);

        this.registerModule(PreloaderModule);
        this.registerModule(LoginModule);
        this.registerModule(MyWalletModule);
        this.registerModule(UserProfileModule);
        this.registerModule(MyBuildingsModule);
        this.registerModule(ShopModule);
        this.registerModule(NotificationMessagesModule);
        this.registerModule(HourlyBonusModule);
        this.registerModule(MarketplaceModule);
        this.registerModule(LatestNewsModule);
        this.registerModule(EthL1PaymentProcessingModule);
        this.registerModule(Erc20PaymentProcessingModule);

        this.internalContainer.registerSingleton("IGameModel", GameModel);
        this.internalContainer.registerSingleton("IGameViewModel", GameViewModel);
        this.internalContainer.registerSingleton("IGameController", GameController);
        this.internalContainer.register("ISettingsModel", SettingsModel, { lifecycle: Lifecycle.ResolutionScoped });
        this.internalContainer.register("ISettingsController", SettingsController);
        this.internalContainer.registerInstance("IContextResolver", this);
        this.internalContainer.register(ResourcesInitializer, {
            useFactory: (_container) => {
                return Promise.resolve(new ResourcesInitializer(this.internalContainer));
            }
        });
        this.internalContainer.register(RaidPopupScene, RaidPopupScene);
        this.internalContainer.register("IRaidController", RaidController);
        this.internalContainer.register("RaidPopupSceneFactory", {
            useValue: async (action: RaidAction) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    RaidPopupScene,
                    new ParamDescription(RaidAction, action)
                );
            }
        });
        this.internalContainer.register(VisitPopupScene, VisitPopupScene);
        this.internalContainer.register("IVisitPopupController", VisitPopupController);
        this.internalContainer.register("VisitPopupSceneFactory", {
            useValue: async (action: VisitAction) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    VisitPopupScene,
                    new ParamDescription(VisitAction, action)
                );
            }
        });
        this.internalContainer.registerSingleton("IDataStorage", LocalStorage);
        this.internalContainer.register(SmallWinCoinsPopupScene, SmallWinCoinsPopupScene);
        this.internalContainer.register("ISmallWinCoinsController", SmallWinCoinsController)
        this.internalContainer.register("SmallWinCoinsPopupSceneFactory", {
            useValue: async (action: DeadRollSmallCoinsAction) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    SmallWinCoinsPopupScene,
                    new ParamDescription(DeadRollSmallCoinsAction, action)
                );
            }
        });
        this.internalContainer.register(MediumWinCoinsPopupScene, MediumWinCoinsPopupScene);
        this.internalContainer.register("IMediumWinCoinsController", MediumWinCoinsController)
        this.internalContainer.register("MediumWinCoinsPopupSceneFactory", {
            useValue: async (action: DeadRollMediumCoinsAction) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    MediumWinCoinsPopupScene,
                    new ParamDescription(DeadRollMediumCoinsAction, action)
                );
            }
        });
        this.internalContainer.register(AmazingWinCoinsPopupScene, AmazingWinCoinsPopupScene);
        this.internalContainer.register("IAmazingWinCoinsController", AmazingWinCoinsController)
        this.internalContainer.register("AmazingWinCoinsPopupSceneFactory", {
            useValue: async (action: AmazingWinAction) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    AmazingWinCoinsPopupScene,
                    new ParamDescription(AmazingWinAction, action)
                );
            }
        });
        this.internalContainer.register(StealPopupScene, StealPopupScene);
        this.internalContainer.register("IStealController", StealController);
        this.internalContainer.register("StealPopupSceneFactory", {
            useValue: async (action: StealAction) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    StealPopupScene,
                    new ParamDescription(StealAction, action)
                );
            }
        });
        this.internalContainer.register(GiftBoxPopupScene, GiftBoxPopupScene);
        this.internalContainer.register("IGiftBoxPopupController", GiftBoxPopupController);
        this.internalContainer.register("GiftBoxPopupSceneFactory", {
            useValue: async (action: DiceComboAction) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    GiftBoxPopupScene,
                    new ParamDescription("DiceComboAction", action)
                );
            }
        });


        this.internalContainer.register(CookiesPopupScene, CookiesPopupScene);
        this.internalContainer.register("CookiesPopupSceneFactory", {
            useValue: async () => {
                return await this.internalContainer.resolve(CookiesPopupScene);
            }
        });
        this.internalContainer.registerSingleton("IGuestModel", GuestModel);
        this.internalContainer.registerSingleton("IApplicationModel", ApplicationModel);
        this.internalContainer.register(UserLevelUpPopupScene, UserLevelUpPopupScene);
        this.internalContainer.register("IUserLevelUpController", UserLevelUpController);
        this.internalContainer.register("UserLevelUpPopupSceneFactory", {
            useValue: async (userLevelUpModel: IUserLevelUpModel) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    UserLevelUpPopupScene,
                    new ParamDescription("IUserLevelUpModel", userLevelUpModel)
                );
            }
        });
        this.internalContainer.register("IPushModel", PushModel, { lifecycle: Lifecycle.ResolutionScoped });
        this.internalContainer.register(PushPopupScene, PushPopupScene);
        this.internalContainer.register("IPushController", PushController);
        this.internalContainer.register("PushPopupSceneFactory", {
            useValue: async (pushDialogData: PushDialogData) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    PushPopupScene,
                    new ParamDescription("PushDialogData", pushDialogData)
                );
            }
        });

        this.internalContainer.registerSingleton("IAuthModel", AuthModel);
        this.internalContainer.register("IAuthController", AuthController);

        //TODO: to shop module
        this.internalContainer.register(CheckOutShopPopupScene, CheckOutShopPopupScene);
        this.internalContainer.register("CheckOutShopPopupSceneFactory", {
            useValue: async () => {
                return await this.internalContainer.resolve(CheckOutShopPopupScene);
            }
        });

        //TODO: seems only part of my buildings module flow
        this.internalContainer.register(CheckOutFixUpgradePopupScene, CheckOutFixUpgradePopupScene);
        this.internalContainer.register("CheckOutFixUpgradePopupSceneFactory", {
            useValue: async () => {
                return await this.internalContainer.resolve(CheckOutFixUpgradePopupScene);
            }
        });

        this.internalContainer.register(DiceRollsOfferPopupScene, DiceRollsOfferPopupScene);
        this.internalContainer.registerSingleton("IDiceRollsOfferModel", DiceRollsOfferModel);
        this.internalContainer.register("IDiceRollOfferController", DiceRollOfferController);
        this.internalContainer.register("DiceRollsOfferPopupSceneFactory", {
            useValue: async (reason: PersonalOfferReason) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    DiceRollsOfferPopupScene,
                    new ParamDescription(String, reason)
                );
            }
        });

        this.internalContainer.register(SocialCommunitiesIntroPopupScene, SocialCommunitiesIntroPopupScene);
        this.internalContainer.register("SocialIntroPopupSceneFactory", {
            useValue: async () => {
                return await this.internalContainer.resolve(SocialCommunitiesIntroPopupScene);
            }
        });

        this.internalContainer.register(SocialCommunitiesPopupScene, SocialCommunitiesPopupScene);
        this.internalContainer.register("SocialPopupSceneFactory", {
            useValue: async () => {
                return await this.internalContainer.resolve(SocialCommunitiesPopupScene);
            }
        });

        this.internalContainer.register(RenterPolicyPopupScene, RenterPolicyPopupScene);
        this.internalContainer.register("RenterPolicyPopupSceneFactory", {
            useValue: async (renterPolicyType: RenterPolicyType, renterPolicyModel?: IRenterPolicyModel) => {
                if (renterPolicyModel) {
                    return await this.resolveWithParams(
                        this.internalContainer,
                        RenterPolicyPopupScene,
                        new ParamDescription(String, renterPolicyType),
                        new ParamDescription("IRenterPolicyModel", renterPolicyModel)
                    );
                } else {
                    return await this.resolveWithParams(
                        this.internalContainer,
                        RenterPolicyPopupScene,
                        new ParamDescription(String, renterPolicyType)
                    );
                }
            }
        });
        this.internalContainer.register(LandlordRenterInfoPopupScene, LandlordRenterInfoPopupScene);
        this.internalContainer.register("LandlordRenterInfoPopupSceneFactory", {
            useValue: async () => {
                return await this.internalContainer.resolve(LandlordRenterInfoPopupScene);
            }
        });
        this.internalContainer.register("IBuildingRankUpController", BuildingRankUpController);
        this.internalContainer.register(BuildingRankUpPopupScene, BuildingRankUpPopupScene);
        this.internalContainer.register("BuildingRankUpPopupSceneFactory", {
            useValue: async (buildingRankUpModel: IBuildingRankUpModel, buildingModel: IBuildingModel, cancellationToken: CancellationToken) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    BuildingRankUpPopupScene,
                    new ParamDescription("IBuildingRankUpModel", buildingRankUpModel),
                    new ParamDescription("IBuildingModel", buildingModel),
                    new ParamDescription(CancellationToken, cancellationToken)
                );
            }
        });
        this.internalContainer.register("IUpgradeItemSceneController", UpgradeItemSceneController);
        this.internalContainer.register(BuildingUpgradeItemPopupScene, BuildingUpgradeItemPopupScene);
        this.internalContainer.register("BuildingUpgradeItemPopupSceneFactory", {
            useValue: async (buildingModel: IBuildingModel, playerModel: IPlayerModel, cancellationToken: CancellationToken) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    BuildingUpgradeItemPopupScene,
                    new ParamDescription("IBuildingModel", buildingModel),
                    new ParamDescription("IPlayerModel", playerModel),
                    new ParamDescription(CancellationToken, cancellationToken)
                );
            }
        });
        this.internalContainer.registerInstance(PiggyAnimationPromiseHolder, new PiggyAnimationPromiseHolder());

        this.internalContainer.register(PiggyPopupScene, PiggyPopupScene);
        this.internalContainer.register("PiggyPopupSceneFactory", {
            useValue: async (state: CurrentPiggyState) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    PiggyPopupScene,
                    new ParamDescription(Number, state)
                );
            }
        });
        this.internalContainer.register("IPiggyController", PiggyController);
        this.internalContainer.register(PiggyInfoPopupScene, PiggyInfoPopupScene);
        this.internalContainer.register("PiggyInfoPopupSceneFactory", {
            useValue: async (type: PiggyInfoType) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    PiggyInfoPopupScene,
                    new ParamDescription(String, type)
                );
            }
        });
        this.internalContainer.register(CheckoutPiggyPopupScene, CheckoutPiggyPopupScene);
        this.internalContainer.register("CheckoutPiggyPopupSceneFactory", {
            useValue: async () => {
                return await this.internalContainer.resolve(CheckoutPiggyPopupScene);
            }
        });

        this.internalContainer.register(DefaultMessagesScene, DefaultMessagesScene);
        this.internalContainer.register("DefaultMessagesSceneFactory", {
            useValue: async (options: DefaultMessageOptions) => {
                return await this.resolveWithParams(
                    this.internalContainer,
                    DefaultMessagesScene,
                    new ParamDescription("DefaultMessageOptions", options)
                );
            }
        });

        this.internalContainer.register(RenterQuestIntroScene, RenterQuestIntroScene);
        this.internalContainer.register("RenterQuestIntroSceneFactory", {
            useValue: async () => {
                return await this.internalContainer.resolve(RenterQuestIntroScene);
            }
        });

        this.internalContainer.register(LeaderboardIntroScene, LeaderboardIntroScene);
        this.internalContainer.register("LeaderboardIntroSceneFactory", {
            useValue: async () => {
                return await this.internalContainer.resolve(LeaderboardIntroScene);
            }
        });

        this.internalContainer.registerSingleton("INewsModel", NewsModel);
        this.internalContainer.registerSingleton(NotificationLayerProvider, NotificationLayerProvider);
        this.internalContainer.registerSingleton(TopPanel, TopPanel);
        this.internalContainer.registerSingleton(AsyncOperationNotification, AsyncOperationNotification);
        this.internalContainer.registerSingleton(AcceptOperationNotification, AcceptOperationNotification);
        this.internalContainer.registerSingleton("IConnectionModel", ConnectionModel);
        this.internalContainer.registerSingleton("IRenterPolicyModel", RenterPolicyModel);
        this.internalContainer.registerSingleton("ILatestNewsModel", LatestNewsModel);
        this.internalContainer.registerSingleton("IPiggyBankModel", PiggyBankModel);
        this.internalContainer.registerSingleton("ITournamentModel", TournamentModel);

        this.internalContainer.register(GiftResourcePackage, GiftResourcePackage);
        this.internalContainer.register(UserProfileResourcePackage, UserProfileResourcePackage);
        this.internalContainer.register(VisitResourcePackage, VisitResourcePackage);
        this.internalContainer.register(AmazingCoinsWinResourcePackage, AmazingCoinsWinResourcePackage);
        this.internalContainer.register(StealResourcePackage, StealResourcePackage);
        this.internalContainer.register(RaidResourcePackage, RaidResourcePackage);
        this.internalContainer.register(UserLevelUpResourcePackage, UserLevelUpResourcePackage);
        this.internalContainer.register(PushResourcePackage, PushResourcePackage);
        this.internalContainer.register(RenterPolicyResourcePackage, RenterPolicyResourcePackage);
        this.internalContainer.register(LandlordRenterInfoResourcePackage, LandlordRenterInfoResourcePackage);
        this.internalContainer.register(SocialCommunitiesResourcePackage, SocialCommunitiesResourcePackage);

        this.registerAnimationBehaviors();
    }

    public async resolve<T>(token: InjectionToken<T>): Promise<T> {
        return await this.internalContainer.resolve(token);
    }

    private async resolveWithParams<T>(container: DependencyContainer, token: InjectionToken<T>, ...args: Array<ParamDescription<any>>): Promise<T> {
        const tempContainer = container.createChildContainer();
        for (const arg of args) {
            tempContainer.registerInstance(arg.token, arg.instance);
        }
        const instance = await tempContainer.resolve(token);
        tempContainer.clearInstances();
        tempContainer.reset();
        return instance;
    }

    private registerModule<TModule extends IModule<TResult>, TResult>(ModuleCls: new (container: DependencyContainer) => TModule): DependencyContainer {
        return this.internalContainer.register(ModuleCls, {
            useFactory: async (container: DependencyContainer) => {
                const module = new ModuleCls(container.createChildContainer());
                await module.build();
                return module;
            }
        });
    }

    private registerAnimationBehaviors(): void {
        Emitter.registerBehavior(RotateToPointBehaviour);
        Emitter.registerBehavior(OrderedTextureWithStartPositionBehavior);
    }
}
